import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';

import {
  cancelOrder,
  deleteDraft,
  finishOrder,
  redirectToReimburseSite,
} from '@/api/orders';
import CancelConfirmation from '@/components/Order/OrderEdit/CancelConfirmation/CancelConfirmation.vue';
import CopyLinkModal from '@/components/Order/CopyLinkModal/CopyLinkModal.vue';
import DisableCustomLinkModal from '@/components/Order/DisableCustomLinkModal/DisableCustomLinkModal.vue';
import DraftDeleteConfirmation from '@/components/Order/OrderEdit/DraftDeleteConfirmation/DraftDeleteConfirmation.vue';
import EnableCustomLinkModal from '@/components/Order/EnableCustomLinkModal/EnableCustomLinkModal.vue';
import ReceiveNotificationModal from '@/components/Order/ReceiveNotificationModal/ReceiveNotificationModal.vue';
import ScheduleListModal from '@/components/Order/ScheduleListModal/ScheduleListModal.vue';
import ScheduleEditModal from '@/components/Order/ScheduleEditModal/ScheduleEditModal.vue';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  FRONT_STAGE_URL,
  ORDER_STATUS_WITH_TYPE_TEXT,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import bus from '@/utils/bus';
import { inReceiveTime } from '@/utils/helper';

export default {
  name: 'OrderList',
  components: {
    CancelConfirmation,
    CopyLinkModal,
    DisableCustomLinkModal,
    DraftDeleteConfirmation,
    EnableCustomLinkModal,
    ReceiveNotificationModal,
    ScheduleListModal,
    ScheduleEditModal,
  },
  props: {
    currentPage: {
      default: FIRST_PAGE,
      type: Number,
    },
    currentPerPage: {
      default: DEFAULT_PER_PAGE,
      type: Number,
    },
    currentTab: {
      default: ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      type: String,
    },
    orders: {
      required: true,
      type: Array,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sortField: {
      default: 'receive',
      type: String,
    },
    sortOrder: {
      default: 'desc',
      type: String,
      validator: (val) => ['asc', 'desc'].includes(val),
    },
  },
  data() {
    return {
      actionOrder: {},
      isCancelingOrder: false,
      isDeletingDraft: false,
      openCancelConfirmation: false,
      openConfirmDeleteDraftDialog: false,
      openCopyLinkModal: false,
      openDisableCustomLinkModal: false,
      openEnableCustomLinkModal: false,
      openNotifyModal: false,
      openScheduleListModal: false,
      openScheduleEditModal: false,
    };
  },
  computed: {
    ...mapGetters(['isProduction']),
    editItem() {
      if (this.inScheduled || this.inEnded || this.inOnGoing) {
        return {
          action: (id) => this.toEditOrder(id),
          icon: 'icon-edit',
          label: '修改',
          tooltip: '修改設定',
        };
      }
      if (this.inDraft) {
        return {
          action: (id) => this.toEditor(id),
          icon: 'icon-edit',
          label: '編輯',
          tooltip: '繼續編輯',
        };
      }
      return {
        action: (id) => this.toEditOrder(id),
        icon: 'icon-lookup',
        label: '查看',
        tooltip: '查看設定',
      };
    },
    emptyText() {
      const statuses = {
        [ORDER_STATUS_WITH_TYPE_TEXT.CANCELLED]: '目前無已取消的訂購',
        [ORDER_STATUS_WITH_TYPE_TEXT.DRAFT]: '目前無草稿',
        [ORDER_STATUS_WITH_TYPE_TEXT.COMPLETED]: '目前無已完成的訂購',
        [ORDER_STATUS_WITH_TYPE_TEXT.ENDED]: '目前無已截止的訂購',
        [ORDER_STATUS_WITH_TYPE_TEXT.ONGOING]: '目前無開放中的訂購',
        [ORDER_STATUS_WITH_TYPE_TEXT.SCHEDULED]: '目前無已公告的訂購',
      };
      return statuses[this.currentTab] || '目前無資料';
    },
    exceptCancelled() {
      return this.currentTab !== ORDER_STATUS_WITH_TYPE_TEXT.CANCELLED;
    },
    exceptDraft() {
      return this.currentTab !== ORDER_STATUS_WITH_TYPE_TEXT.DRAFT;
    },
    inCancelled() {
      return this.currentTab === ORDER_STATUS_WITH_TYPE_TEXT.CANCELLED;
    },
    inCompleted() {
      return this.currentTab === ORDER_STATUS_WITH_TYPE_TEXT.COMPLETED;
    },
    inDraft() {
      return this.currentTab === ORDER_STATUS_WITH_TYPE_TEXT.DRAFT;
    },
    inEnded() {
      return this.currentTab === ORDER_STATUS_WITH_TYPE_TEXT.ENDED;
    },
    inOnGoing() {
      return this.currentTab === ORDER_STATUS_WITH_TYPE_TEXT.ONGOING;
    },
    inScheduled() {
      return this.currentTab === ORDER_STATUS_WITH_TYPE_TEXT.SCHEDULED;
    },
    note() {
      if (this.inCompleted) {
        return '*僅顯示過去30天內完成的訂購資料';
      }
      if (this.inCancelled) {
        return '*僅顯示過去30天內取消的訂購資料';
      }
      return '';
    },
  },
  methods: {
    dayjs,
    async deleteDraft() {
      this.isDeletingDraft = true;
      try {
        await deleteDraft(this.actionOrder.id);
        this.setActionOrder({});
        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '草稿已刪除',
        });
        bus.$emit('GET_ORDERS');
        this.openConfirmDeleteDraftDialog = false;
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '刪除草稿時發生錯誤，請重試一次',
          msg,
        });
      } finally {
        this.isDeletingDraft = false;
      }
    },
    extraReceiveTimes(times = []) {
      const newArr = cloneDeep(times);
      newArr.shift();
      return newArr;
    },
    getExtraItems(order) {
      return [
        {
          action: () => {
            this.toEditMenu(order);
          },
          label: '修改菜單',
          show: this.inScheduled,
        },
        {
          action: () => {
            this.openCopyLinkModal = true;
            this.setActionOrder(order);
          },
          label: '分享訂購資訊',
          show: this.inOnGoing || this.inScheduled,
        },
        {
          action: () => {
            this.openNotifyModal = true;
            this.setActionOrder(order);
          },
          label: '發送領取通知',
          show: this.inOnGoing || this.inEnded,
        },
        {
          action: () => {
            const { share: { share_key: shareKey = '' } = {} } = order;
            this.$router.push(`/temp-report/${shareKey}`);
          },
          label: '檢視即時訂購統計',
          show: this.inOnGoing || this.inEnded || this.inScheduled,
        },
        {
          action: () => {
            this.openDisableCustomLinkModal = true;
            this.setActionOrder(order);
          },
          label: '停用訪客訂購連結',
          show: (this.inOnGoing || this.inEnded) && order?.share?.share_status,
        },
        {
          action: () => {
            this.openEnableCustomLinkModal = true;
            this.setActionOrder(order);
          },
          label: '啟用訪客訂購連結',
          show: (this.inOnGoing || this.inEnded) && !order?.share?.share_status,
        },
        {
          action: () => {
            this.onOpenUrl(order);
          },
          label: '開啟核銷網址',
          show: (this.inOnGoing || this.inEnded) && inReceiveTime(order.receive_times),
        },
        {
          action: () => {
            this.openCancelConfirmation = true;
            this.setActionOrder(order);
          },
          label: '取消一起訂',
          labelClasses: 'text-red-500',
          show: true,
        },
      ];
    },
    getLimitation(limit = {}) {
      const { limit_item: item = 0, limit_money: money = 0 } = limit || {};
      if (!item && !+money) {
        return '';
      }
      const count = this.$options.filters.commaFormat(item || +money || 0);
      return item ? `${count} 項` : `$${count}`;
    },
    getShareLink(order = {}) {
      const domain = this.isProduction ? FRONT_STAGE_URL.PRODUCTION : FRONT_STAGE_URL.STAGING;
      return `${domain}/menu/${order.id}?share=${order?.share?.share_key}`;
    },
    isShareModeOff(order = {}) {
      return !this.isShareModeOn && order?.report?.ordered_guest_count > 0;
    },
    isShareModeOn(order = {}) {
      return order?.share?.share_status;
    },
    async onDraftDelete(order = {}) {
      this.openConfirmDeleteDraftDialog = true;
      this.setActionOrder(order);
    },
    async onOrderCancel() {
      this.isCancelingOrder = true;
      try {
        await cancelOrder(this.actionOrder?.id);
        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '團購已取消',
        });
        this.setActionOrder({});
        bus.$emit('GET_ORDERS');
        this.openCancelConfirmation = false;
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '取消團購時發生錯誤，請重試一次',
          msg,
        });
      } finally {
        this.isCancelingOrder = false;
      }
    },
    async onOrderFinish(order = {}) {
      try {
        await finishOrder(order.id);
        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '團購已完成',
        });
        bus.$emit('GET_ORDERS');
      } catch (msg) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '完成團購時發生錯誤，請重試一次',
          msg,
        });
      }
    },
    async onOpenUrl(order = {}) {
      try {
        const { data: { url = '/' } = {} } = await redirectToReimburseSite(order.id);
        window.open(url, '_blank');
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '開啟核銷網址時發生錯誤，請重試一次',
          msg,
        });
      }
    },
    onSort(field, order) {
      this.$emit('sort', field, order);
    },
    setActionOrder(order = {}) {
      this.actionOrder = order || {};
    },
    toggleScheduleModel(show = false, order = {}) {
      const { schedule: { version: scheduleVersion } = {} } = order;
      this.setActionOrder({});
      this.setActionOrder(order);
      if (scheduleVersion === 2) {
        this.openScheduleEditModal = show;
      } else {
        this.openScheduleListModal = show;
      }
    },
    toEditor(id) {
      this.$router.push({
        path: `/order/create/${id}`,
        query: {
          tab: this.currentTab,
        },
      });
    },
    toEditMenu(order = {}) {
      this.$router.push({
        path: `/store/${order?.store_id}/menus/group_buyings`,
        query: {
          order_id: order?.id,
          tab: this.currentTab,
        },
      });
    },
    toEditOrder(id) {
      this.$router.push({
        path: `/order/edit/${id}`,
        query: {
          tab: this.currentTab,
        },
      });
    },
    toOrderOverview(id) {
      this.$router.push({
        path: `/order/${id}`,
        query: {
          tab: this.currentTab,
        },
      });
    },
  },
};
